import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import { Form, Radio } from "antd";
import {
  ControlOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  ErrorResults,
  InputFactory,
  CardSkeletons,
  SectionGroupButtons,
  RowSize,
  ColSize,
  SectionGroupButtonsRef,
} from "../../../shared/components";

import { Grid } from "@hdi-seguros/web-components.grid";
import { InsurancePolicyCard, PolicyNotFound } from "./components";
import { PageHeader } from "../../../shared/components/MainLayout/components";

import InsurancePolicyService from "../../../shared/services/insurancePolicy";
import MasterService from "../../../shared/services/master";

import { createXLSXFile } from "../../../shared/utils";

import { AuthContext, InfoContext, UIContext } from "../../../shared/contexts";

import {
  AlertsForProducersWeb,
  Combo,
  InputFactoryProp,
  InsurancePolicy,
  ORDER_BY,
} from "../../../shared/interfaces";

import { COLORS } from "../../../config/themes/colors";
import { getAlertsButtons, mapInfoOfPolicies } from "./utils";
import { InsurancePolicyListGroupButtons } from "./interfaces";

import { InsurancePolicyList } from "../../../shared/interfaces/services/insurance-policy";
import { GetPoliciesOfXlsx } from "../../../shared/interfaces/services/get-policies-of-xlsx";

import "./index.css";
import { RenewalQuotesProvider } from "../../quotation/SavedQuotes/shared/contexts/RenewalQuotes";
import { TourComponent } from "../../../shared/components/Tour";
import { AlertComponent } from "../../../shared/components/AlertComponent";
import { parseCurrencyValue } from "../../../shared/utils/parseCurrencyValue";
import { FILTER_POLICY } from "../../../shared/interfaces/enums/filter-policy.enum";

const CURRENCY_COLUMNS = [
  "Prima Técnica",
  "Recargos",
  "Prima Total",
  "Premio",
  "Comisión Productor",
  "Comisión Organizador",
  "Comisión Productor 2",
  "Comisión Productor 3",
];

interface InsurancePolicyListProps {
  title: string;
}

export const InsurancePolicyListPage: FC<InsurancePolicyListProps> = ({
  title,
}) => {
  const { isLoading, showError, setIsLoading, setShowError } =
    useContext(UIContext);
  const { setTitle } = useContext(InfoContext);

  const gridRef = useRef();
  const buttonsRef = useRef<SectionGroupButtonsRef>(null);

  const [statesCombo, setStatesCombo] = useState<Combo[]>([]);
  const [producersCombo, setProducersCombo] = useState<Combo[]>([]);
  const [branchesCombo, setBranchesCombo] = useState<any>([]);
  const [insuredCombo, setInsuredCombo] = useState<Combo[]>([]);
  const authContext = useContext(AuthContext);

  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  const [insurancePolicyListResponse, setInsurancePolicyListResponse] =
    useState<InsurancePolicyList>({});

  const [insurancePolicyListXlsxResponse, setInsurancePolicyListXlsxResponse] =
    useState<GetPoliciesOfXlsx | null>(null);

  const [policyList, setPolicyList] = useState<Array<InsurancePolicy>>(
    insurancePolicyListResponse.polizas || []
  );

  const [alerts, setAlerts] = useState<AlertsForProducersWeb>();
  const [isLoadingAlerts, setIsLoadingAlerts] = useState<boolean>(false);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [filter, setFilter] = useState<Number>(0);
  const [hideDateFilter, setHideDateFilter] = useState(false);
  const [primaryFiltersDisabled, setPrimaryFiltersDisabled] = useState<boolean>(false);
  const [form] = Form.useForm();
  const insuredWatch = Form.useWatch("insured", form);
  const branchWatch = Form.useWatch("branch", form);
  const policyNumberWatch = Form.useWatch("policyNumber", form);

  const getInsurancePolicyList = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);

    buttonsRef?.current?.deactivateButtons();

    try {
      const filters = form.getFieldsValue();

      const response = await InsurancePolicyService.getInsurancePolicies(
        filters
      );

      if (response.data) {
        setInsurancePolicyListResponse(response.data);
      }
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, form, setInsurancePolicyListResponse]);

  const firstDay: string = dayjs().subtract(1, "month").format("DD-MM-YYYY");
  const dateFormat: string = "DD-MM-YYYY";

  const initialStateFilters = useMemo(
    () => ({
      insured: [],
      branch: [],
      policyNumber: "",
      producer: [],
      date: primaryFiltersDisabled ? [dayjs().subtract(1, 'year'), dayjs()] : [dayjs(firstDay, dateFormat), dayjs()],
      state: [],
      orderBy: ORDER_BY.MOST_RECENT_FIRST,
    }),
    [primaryFiltersDisabled]
  );

  const stepsTour = [
    {
      target: ".step-1",
      content: (
        <p>
          ✅ Filtrá tus pólizas fácilmente. Usá estos filtros para buscar
          pólizas por número, fechas, estado, ramo, asegurado y más. Combina
          varios criterios para obtener resultados más precisos y ahorrar
          tiempo.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-2",
      content: (
        <p>
          🔢 Encontra la póliza que buscas. Ingresa el número completo de
          póliza. Para usar este filtro es obligatorio seleccionar primero la{" "}
          <b>Rama.</b>
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-3",
      content: (
        <p>
          📅 Acotá la búsqueda por período de Emisión que por defecto muestra 30
          días ! Selecciona desde qué fecha y hasta cuándo queres buscar. Esto
          aplica a todos los filtros, incluso cuando buscas por número de
          póliza, recorda mientras mas extensa en la diferencia de fecha mas se
          demora en filtrar
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-4",
      content: (
        <p>
          📂 Filtra según el estado actual Mostra solo pólizas vigentes,
          canceladas, vencidas o en proceso. Ideal para hacer seguimiento de
          acciones o detectar vencimientos.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-5",
      content: (
        <p>
          🧾 Ajustá tu búsqueda por tipo de seguro Seleccioná la rama
          correspondiente (Autos, Hogar, Vida, etc.). Este filtro es obligatorio
          si querés buscar por número de póliza.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-6",
      content: (
        <p>
          👤 Busca por nombre, apellido o razón social. Es útil cuando no tenés
          el número de póliza a mano. Siempre se busca dentro de las fechas de
          emisión seleccionadas
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-7",
      content: <p>🧑‍💼 Filtra dentro de tu red de productores</p>,
      disableBeacon: true,
    },
    {
      target: "body",
      content: (
        <p>
          💡 Combina filtros para mejores resultados. Por ejemplo: Pólizas de
          Autos vigentes, emitidas en el último mes. Cuanto más específico seas,
          más rápido llegarás a la información que necesitas
        </p>
      ),
      disableBeacon: true,
    },
  ];

  const inputsFilter: InputFactoryProp[] = useMemo(
    () => [
       {
        element: "select",
        isMulti: true,
        placeHolder: "Asegurado",
        items: insuredCombo,
        name: "insured",
        className: "step-6",
         hidden: filter !== FILTER_POLICY.BY_INSURED
      },
      {
        element: "select",
        isMulti: true,
        placeHolder: "Ramo",
        items: branchesCombo,
        name: "branch",
        className: "step-5",
        hidden: filter !== FILTER_POLICY.BY_BRANCH_AND_POLICY_NUMBER
      },
      {
        element: "input",
        type: "number",
        placeHolder: "Número de póliza",
        icon: <SearchOutlined style={{ color: COLORS.neutrals[50] }} />,
        name: "policyNumber",
        className: "step-2",
        hidden: filter !== FILTER_POLICY.BY_BRANCH_AND_POLICY_NUMBER
      },
      {
        element: "input",
        type: "text",
        placeHolder: "Número de patente",
        icon: <SearchOutlined style={{ color: COLORS.neutrals[50] }} />,
        name: "licensePlate",
        className: "step-2",
        hidden: filter !== FILTER_POLICY.BY_LICENCE_PLATE,
        maxLength: 25,
      },
      {
        element: "select",
        isMulti: true,
        placeHolder: "Productor",
        items: producersCombo,
        name: "producer",
        className: "step-7",
      },
      {
        element: "date",
        name: "date",
        className: "step-3",
        tooltip: "Fecha de emisión",
        hidden: hideDateFilter || !(filter !== FILTER_POLICY.BY_LICENCE_PLATE)
      },
      {
        element: "select",
        isMulti: true,
        placeHolder: "Estado",
        items: statesCombo,
        name: "state",
        className: "step-4",
      },
      {
        element: "select",
        placeHolder: "Ordenar por",
        items: [
          { label: "Más reciente primero", value: ORDER_BY.MOST_RECENT_FIRST },
          { label: "Más antigua primero", value: ORDER_BY.OLDEST_FIRST },
          { label: "Orden alfabético", value: ORDER_BY.ALPHABETICAL_ORDER },
        ],
        name: "orderBy",
        className: "step-4",
      },
    ],
    [statesCombo, producersCombo, branchesCombo, insuredCombo, filter, hideDateFilter]
  );

  const mapMovementsToExcelFormat = useCallback((movements: any[]) => {
    const userLevel = (authContext as any)?.intermediarioPrincipal?.nivelInter || 1;
    
    return movements.map((movement) => {
      const baseData: any = {
        "Cód. Rama": movement.rama,
        "Rama Descripción": movement.ramaDescripcion,
        "Póliza": movement.poliza,
        "Supl.": movement.suplemento,
        "Operación": movement.tipoDeOperacion,
        "Asegurado": movement.nombreAsegurado,
        "Cód. Productor": movement.codigoProductor,
        "Productor": movement.nombreProductor,
        "Fecha Emisión": movement.fechaEmision ?? '',
        "Vigencia Desde": movement.vigenciaDesde ?? '',
        "Vigencia Hasta": movement.vigenciaHasta ?? '',
        "Patente": movement.patente,
        "Prima Técnica": parseCurrencyValue(movement.prima),
        "Recargos": parseCurrencyValue(movement.recargos),
        "Prima Total": parseCurrencyValue(movement.primaTotal),
        "Premio": parseCurrencyValue(movement.premio),
      };

      if (userLevel >= 1) {
        baseData["Comisión Productor"] = parseCurrencyValue(movement.comisionProductor);
      }
      
      if (userLevel >= 3) {
        baseData["Comisión Organizador"] = parseCurrencyValue(movement.comisionOrganizador);
      }
      
      if (userLevel >= 5) {
        baseData["Comisión Productor 2"] = parseCurrencyValue(movement.comisionProductorDos);
      }
      
      if (userLevel >= 6) {
        baseData["Comisión Productor 3"] = parseCurrencyValue(movement.comisionProductorTres);
      }

      return baseData;
    });
  }, [authContext]);

  const handleDownloadExcel = useCallback(async () => {
    try {
      const filters = form.getFieldsValue();
      
      const response = await InsurancePolicyService.getPoliciesOfXlsx(filters);
      if (response.data && response.data.movimientos) {
        const excelData = mapMovementsToExcelFormat(response.data.movimientos);
        createXLSXFile(excelData, "Listado de Pólizas Excel", { currencyColumns: CURRENCY_COLUMNS, textColumns: ["Fecha Emisión", "Vigencia Desde", "Vigencia Hasta"] });
      }
    } catch (error) {
      setShowError(true);
    }
  }, [mapMovementsToExcelFormat, setShowError, form]);

  const actions = useMemo(
    () => [
      {
        label: "Descargar",
        className: "btn-accent-outline-barbuss",
        icon: <DownloadOutlined />,
        onClick: handleDownloadExcel,
      },
    ],
    [handleDownloadExcel]
  );

  const getComboStates = useCallback(async () => {
    try {
      const response = await MasterService.getStatesForCombo();

      if (response.status === 200) setStatesCombo(response.data);
    } catch (error) {}
  }, [setStatesCombo]);

  const getComboBranch = useCallback(async () => {
    try {
      const response = await MasterService.getBranchesForCombo();

      if (response.status === 200) setBranchesCombo(response.data);
    } catch (error) {}
  }, [setBranchesCombo]);

  const getComboInsured = useCallback(async () => {
    try {
      const response = await MasterService.getInsuredForCombo();

      if (response.status === 200) {
        setInsuredCombo(response.data);
      } else {
        setInsuredCombo([]);
      }
    } catch (error) {
      setInsuredCombo([]);
    }
  }, [setInsuredCombo]);

useEffect(() => {
  const hasInsured = Array.isArray(insuredWatch) && insuredWatch.length > 0;
  const hasBranch = Array.isArray(branchWatch) && branchWatch.length > 0;
  const hasPolicyNumber =
    policyNumberWatch !== undefined &&
    policyNumberWatch !== null &&
    String(policyNumberWatch).trim() !== "";
  setHideDateFilter(hasInsured || hasBranch || hasPolicyNumber);

}, [insuredWatch, branchWatch, policyNumberWatch]);

  const getComboProducer = useCallback(async () => {
    try {
      const response =
        await MasterService.getIntermediariesByIntermediaryCombo();

      if (response.status === 200) setProducersCombo(response.data);
    } catch (error) {}
  }, [setProducersCombo]);

  const getAlerts = useCallback(async () => {
    setIsLoadingAlerts(true);
    try {
      const response =
        await InsurancePolicyService.getAlertsForInsurancePolicyList();

      if (response.data) {
        setAlerts(response.data);
      }
    } catch (error) {
    } finally {
      setTimeout(() => setIsLoadingAlerts(false), 2000);
    }
  }, [setAlerts, setIsLoadingAlerts]);

  const getFiltersInfo = useCallback(() => {
    Promise.all([
      getComboStates(),
      getComboBranch(),
      getComboProducer(),
      getComboInsured(),
      getAlerts(),
    ])
      .then(() => {
        setIsLoadingFilters(false);
      })
      .catch((error) => {});
  }, [
    getComboStates,
    getComboBranch,
    getComboProducer,
    setIsLoadingFilters,
    getAlerts,
  ]);

  const getPoliciesSoonToExpire = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const response = await InsurancePolicyService.getPoliciesSoonToExpire();

      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getPoliciesOfXlsx = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const filters = form.getFieldsValue();
      const response = await InsurancePolicyService.getPoliciesOfXlsx(filters);

      if (response.data) {
        setInsurancePolicyListXlsxResponse(response.data);
      }
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListXlsxResponse, form]);

  const getPoliciesGroupLifeNotUpdated = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const response =
        await InsurancePolicyService.getPoliciesGroupLifeNotUpdated();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getPoliciesGroupLifeLTC = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const response = await InsurancePolicyService.getPoliciesGroupLifeLTC();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getPoliciesExpiredAndNotRenewed = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);
    try {
      const response =
        await InsurancePolicyService.getPoliciesExpiredAndNotRenewed();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getPoliciesLastEndorsementReversal = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);
    try {
      const response =
        await InsurancePolicyService.getPoliciesLastEndorsementReversal();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getPoliciesPendingRenewalDueToBalance = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);
    try {
      const response =
        await InsurancePolicyService.getPoliciesPendingRenewalDueToBalance();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const getWithdrawalRequest = useCallback(async () => {
    setIsLoading(true);
    setShowError(false);
    try {
      const response =
        await InsurancePolicyService.getWithdrawalRequest();
      if (response.data) setInsurancePolicyListResponse(response.data);
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setShowError, setInsurancePolicyListResponse]);

  const onPressCard = useCallback(
    async (id: string) => {
      setPrimaryFiltersDisabled(true)
      form.setFieldsValue(initialStateFilters);
      switch (id) {
        case InsurancePolicyListGroupButtons.POLICIES_SOON_TO_EXPIRE:
          await getPoliciesSoonToExpire();
          break;
        case InsurancePolicyListGroupButtons.EXPIRED_AND_NOT_RENEWED:
          await getPoliciesExpiredAndNotRenewed();
          break;
        case InsurancePolicyListGroupButtons.GROUP_LIFE_LCT:
          await getPoliciesGroupLifeLTC();
          break;
        case InsurancePolicyListGroupButtons.GROUP_LIFE_NOT_UPDATED:
          await getPoliciesGroupLifeNotUpdated();
          break;
        case InsurancePolicyListGroupButtons.PENDING_RENEWAL_DUE_TO_BALANCE:
          await getPoliciesPendingRenewalDueToBalance();
          break;
        case InsurancePolicyListGroupButtons.LAST_ENDORSEMENT_REVERSAL:
          await getPoliciesLastEndorsementReversal();
          break;
        case InsurancePolicyListGroupButtons.WITHDRAWAL_REQUESTS:
          await getWithdrawalRequest();
          break;
        case InsurancePolicyListGroupButtons.ENDORSEMENTS_INCREASE_AUTOS:
          break;
        case InsurancePolicyListGroupButtons.ENDORSEMENTS_INCREASE_ELEVATORS_BOILERS:
          break;
        default:
          break;
      }
    },
    [
      getPoliciesSoonToExpire,
      getPoliciesExpiredAndNotRenewed,
      getPoliciesGroupLifeLTC,
      getPoliciesGroupLifeNotUpdated,
    ]
  );

  const onDesactivateCard = useCallback(() => {
    setPrimaryFiltersDisabled(false)
    getInsurancePolicyList();
  }, [getInsurancePolicyList]);
  const onUpdateCard = useCallback(
    () => getInsurancePolicyList(),
    [getInsurancePolicyList]
  );

  const buttons = useMemo(
    () => (alerts ? getAlertsButtons(alerts) : []),
    [alerts]
  );

  useEffect(() => {
    setTitle(title);
    getFiltersInfo();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (insurancePolicyListResponse) {
      (gridRef.current as any)?.setPage(1);
    }
  }, [insurancePolicyListResponse, setInsurancePolicyListResponse]);

  useEffect(() => {    
    form.setFieldsValue(initialStateFilters);
  }, [form, initialStateFilters]);

  const toggleTour = () => {
    setShowTour(!showTour);
  };

  return (
    <RenewalQuotesProvider>
      {isLoadingFilters ? null : (
        <TourComponent
          showTour={showTour}
          steps={stepsTour}
          onTourClose={toggleTour}
        />
      )}
      <div className="insurence-policy-list-container step-1">
        <PageHeader activeTour={toggleTour} actions={actions} />
        <RowSize
          rowProps={{
            align: "middle",
            gutter: [0, 0],
          }}
          className="row-insurance-policy-list"
        >
          
          { primaryFiltersDisabled && (
            <AlertComponent
              message="Si se están usando los accesos directos, no se puede filtrar"
              type="info"
              className="insured-policy-list_alert-filters"
            />
          )}
          <RowSize className="mb-2">
            <ColSize size={100}>
              <Form.Item label="Filtrar por">
                <Radio.Group
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    form.setFieldsValue({
                      insured: [],
                      branch: [],
                      policyNumber: ""
                    });
                  }}
                >
                  <Radio value={FILTER_POLICY.BY_INSURED}>Asegurado</Radio>
                  <Radio value={FILTER_POLICY.BY_BRANCH_AND_POLICY_NUMBER}>Ramo y Nº de Póliza</Radio>
                  <Radio value={FILTER_POLICY.BY_LICENCE_PLATE}>Patente</Radio>
                </Radio.Group>
              </Form.Item>
            </ColSize>
          </RowSize>

          <InputFactory
            form={form}
            items={inputsFilter}
            onClear={getInsurancePolicyList}
            onFilter={getInsurancePolicyList}
            isLoading={isLoadingFilters}
            initialState={initialStateFilters}
            isDisable={primaryFiltersDisabled}
          />
        </RowSize>
        <div className="step-6">
          <SectionGroupButtons
            title="Accesos directos"
            buttons={buttons}
            onPressButton={onPressCard}
            onDesactiveButton={onDesactivateCard}
            buttonsJustify="center"
            isLoading={isLoadingAlerts}
            ref={buttonsRef}
          />
        </div>

        {showError ? (
          <ErrorResults title="No se pudo cargar el listado de pólizas" />
        ) : (
          <RowSize
            rowProps={{
              gutter: [0, 0],
            }}
          >
            <ColSize size={100}>
              <Grid
                list={insurancePolicyListResponse?.polizas || []}
                setState={setPolicyList}
                name="pólizas"
                ref={gridRef}
              >
                <ColSize size={100}>
                  {policyList?.length ? (
                    policyList.map((insurancePolicy, index) => (
                      <InsurancePolicyCard
                        key={`insurancePolicyCard-${insurancePolicy.nroPoliza}-${index}`}
                        insurancePolicy={insurancePolicy}
                        loading={isLoading}
                        onUpdate={onUpdateCard}
                      />
                    ))
                  ) : isLoading ? (
                    <RowSize>
                      <CardSkeletons />
                    </RowSize>
                  ) : (
                    <PolicyNotFound />
                  )}
                </ColSize>
              </Grid>
            </ColSize>
          </RowSize>
        )}
      </div>
    </RenewalQuotesProvider>
  );
};
